// Hero.tsx
import React from "react";
import {
  View,
  StyleSheet,
  Platform,
  ImageBackground,
  Pressable,
} from "react-native";
import radial from "./backgrounds/radial.svg";
import variables from "../../styles/variables";
import { useResponsive } from "../../hooks/useResponsive";
import ComingupBar from "../ComingupBar/ComingupBar";
import { useAtom } from "jotai";
import { advertisingAtom, pageDataAtom } from "../../atoms";
import AdBanner from "../Layout/AdBanners/AdBanner";
import { advertClick } from "../../client/adverts";
import { SingleCarouselItem } from "../Screens/Members/components/Carousel";
import lazyImport from "../../utils/lazyImport/lazyImport";
import RSuspense from "../../utils/RSuspense/RSuspense";
import { CarouselItem } from "../../types/page";

const ReanimatedCarouselAnimation = lazyImport(() => {
  return import("../Animations/ReanimatedCarousel/ReanimatedCarouselAnimation");
});

const { colors } = variables;

export type HeroProps = {
  children?: React.ReactNode;
};

export const HeroBackgroundWrapper = () => {
  const { isMediumAndUp } = useResponsive();

  return (
    <View
      style={{
        justifyContent: "flex-start",
        // this wraps the hero to make it full width yet 40% height. It cant be done within the hero
        width: "100%",
        maxHeight: "80%",
      }}
    >
      <View>
        {/* Above empty wrapping view must exist */}
        <HeroBackground>
          <Hero />
          {isMediumAndUp && (
            <ComingupBar
              style={{
                position: "absolute",
                bottom: 0,
              }}
            />
          )}
        </HeroBackground>
      </View>
    </View>
  );
};

export const HeroBackground = ({ children }: any) => {
  const { isSmallAndDown } = useResponsive();
  const RadialComponent = radial;
  return (
    <>
      <RadialComponent
        style={{
          position: "absolute",
          left: 0,
          bottom: 0,
          right: 0,
          top: 0,
          minWidth: "100%",
          maxWidth: "100%",
          height:
            Platform.OS === "web" ? "100%" : isSmallAndDown ? "180%" : "280%",
          minHeight:
            Platform.OS === "web" ? "100%" : isSmallAndDown ? "180%" : "280%",
        }}
      />
      <ImageBackground
        source={require("../../assets/r.webp")}
        resizeMode="cover"
        style={{
          position: "absolute",
          left: 0,
          bottom: 0,
          width: "100%",
          maxWidth: 600,
          height: "100%",
          minHeight: "100%",
        }}
      />
      {children}
    </>
  );
};

export default function Hero({
  heroData,
  isAttachment = false,
}: {
  heroData?: CarouselItem[];
  isAttachment?: boolean;
}) {
  const { isXSmallOnly, isXLargeAndUp } = useResponsive();
  const [adverts] = useAtom(advertisingAtom);
  const { page } = useAtom(pageDataAtom)[0] ?? {};

  const heroColor = isAttachment
    ? colors.palette.rtv.primary
    : colors.palette.neutral.x0;

  const carouselItems = [];
  (heroData ?? page?.carousel_items)
    ?.sort((a, b) => a.priority - b.priority)
    .map((item, index) => {
      carouselItems.push(
        <View
          style={[
            styles.carouselItem,
            {
              backgroundColor: heroColor,
              borderColor: heroColor,
            },
          ]}
        >
          <SingleCarouselItem
            carouselItem={item}
            position={index}
            loading={index < 1 ? "eager" : "lazy"}
            isAttachment={isAttachment}
          />
        </View>
      );
    });

  const HeroRow = () => {
    const paddingHorizontal = isAttachment ? 0 : isXLargeAndUp ? "15%" : 40;
    const paddingVertical = isAttachment ? 0 : 40;

    const data = heroData || page?.carousel_items;

    return (
      <View
        style={[
          styles.rowContainer,
          {
            flex: isAttachment ? undefined : 1,
            height: isAttachment ? undefined : "100%",
            paddingHorizontal: paddingHorizontal,
            paddingVertical: paddingVertical,
          },
        ]}
      >
        <View
          style={[
            styles.rowCol1,
            {
              paddingRight: 18,
            },
          ]}
        >
          {/* TODO: check priority once cms bug is fixed */}
          {/* priority 1 content */}
          <View
            style={[
              { backgroundColor: heroColor, borderColor: heroColor },
              styles.col1Box,
            ]}
          >
            <SingleCarouselItem
              carouselItem={data?.length ? data?.[0] : null}
              position={0}
              loading={"eager"}
              isAttachment={isAttachment}
            />
          </View>
        </View>
        <View style={styles.rowCol2}>
          {/* priority 2 content */}
          <View
            style={[
              {
                backgroundColor: heroColor,
                borderColor: heroColor,
                height: isAttachment ? undefined : "48%",
              },
              styles.col2Box,
            ]}
          >
            <SingleCarouselItem
              carouselItem={data?.length ? data?.[1] : null}
              position={1}
              loading={"eager"}
              isAttachment={isAttachment}
            />
          </View>
          {/* priority 3 content */}
          <View
            style={[
              {
                backgroundColor: heroColor,
                borderColor: heroColor,
                height: isAttachment ? undefined : "48%",
              },
              styles.col2Box,
            ]}
          >
            <SingleCarouselItem
              carouselItem={data?.length ? data?.[2] : null}
              position={2}
              loading={"eager"}
              isAttachment={isAttachment}
            />
          </View>
        </View>
      </View>
    );
  };

  // Pushes adverts into carousel on small screens
  if (adverts?.carousel) {
    carouselItems.push(
      <Pressable
        onPress={() => advertClick(adverts.carousel.link_url)}
        style={styles.carouselItem}
      >
        <AdBanner
          resizeMode="contain"
          style={[
            styles.vod,
            Platform.OS == "web" ? { cursor: "pointer" } : {},
          ]}
          ad={adverts.carousel}
        />
      </Pressable>
    );
  }

  // Carousel for smaller screens, row view for bigger screens
  return (
    <>
      {isXSmallOnly ? (
        <RSuspense>
          <></>
          <ReanimatedCarouselAnimation
            items={carouselItems}
            isAttachment={isAttachment}
          />
        </RSuspense>
      ) : (
        <HeroRow />
      )}
    </>
  );
}

const styles = StyleSheet.create({
  rowContainer: {
    width: "100%",
    flexDirection: "row",
    justifyContent: "space-between",
  },
  rowCol1: {
    flex: 2.14, // this is the ratio of the col1 to col2, where content is ratio of 16/9
  },
  rowCol2: {
    display: "flex",
    justifyContent: "space-between",
    flex: 1,
  },
  carouselItem: {
    borderRadius: 5,
    alignSelf: "center",
    width: "90%",
    padding: 2,
    // paddingTop: 0, // this padding arrangement is a bit wierd but it looks the best accross devices
    aspectRatio: 16 / 9,
    marginTop: "auto",
    marginBottom: "auto",
  },
  col1Box: {
    aspectRatio: 16 / 9,
    borderWidth: 2,
    borderRadius: 4,
    justifyContent: "center",
    marginRight: 15,
    minWidth: "100%",
    width: "100%",
  },
  col2Box: {
    aspectRatio: 16 / 9,
    borderWidth: 1,
    borderRadius: 4,
    // paddingTop: 2,
    justifyContent: "center",
    width: "100%",
  },
  play: {
    alignSelf: "center",
  },
  vod: {
    justifyContent: "center",
    alignContent: "center",
    alignItems: "center",
    width: "100%",
    height: "100%",
  },
});
